/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    ActionTagApi,
    Configuration,
    ConfigurationParameters,
    ContentScreenDetails,
    OfferActionsGetV2Request,
    RecordingActionsGetV2Request,
    VodOfferActionsGetV2Request,
} from 'client-code/actions';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IActionsServiceRepository } from '../interfaces';
import { Log } from '../logging';
import {
    TActionList,
    TActionsContentParams,
    TActionsContentScreenContentIdParams,
    TActionsContentScreenOfferParams,
    TActionsContentScreenRecordingParams,
    TActionsParams,
    TActionsParamsUCScreenDetails,
    TActionsRecordingParams,
    TActionsUnifiedCollectionParams,
    TActionsVodOfferParams,
    TCustomActionList,
    TServicesParams,
    TUnifiedCollectionScreenDetails,
} from '../types';
import { UniqueIdentifier } from '../utils';

export class ActionsServiceRepositoryImplV2 implements IActionsServiceRepository {
    private static TAG: string = 'ActionsServiceRepositoryImplV2';

    private mParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private getConfigurationParameters(commonHeaders) {
        const configurationParameters: ConfigurationParameters = {
            apiKey: this.mParams.accessToken,
            basePath: this.mParams.basePath,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: commonHeaders,
            },
        };
        return configurationParameters;
    }

    private getRequestParameters(commonHeaders, groups, channelNumber) {
        return {
            applicationName: commonHeaders.ApplicationName,
            channelNumber,
            deviceType: commonHeaders.DeviceType,
            groups,
            productName: commonHeaders.ProductName,
            acceptLanguage: commonHeaders['Accept-Language'],
            applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
            applicationVersion: commonHeaders.ApplicationVersion,
            timeZoneName: this.mParams.timeZoneName,
            bodyId: this.mParams.bodyId,
            xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            whiny: this.mParams.whiny || 'false',
        };
    }

    private async getOfferActionsFromServiceV2(
        actionsParams: TActionsParams,
    ): Promise<TActionList> {
        try {
            const { offerId, groups, channelNumber, screenType, actionsNetworkCallId } =
                actionsParams;
            const commonHeaders = new CommonHeaders(screenType).headers;
            const requestParameters: OfferActionsGetV2Request = {
                offerId,
                ...this.getRequestParameters(commonHeaders, groups, channelNumber),
            };
            const configuration = new Configuration(this.getConfigurationParameters(commonHeaders));
            const actionsApi = new ActionTagApi(configuration);
            const actionsApiResponseObj = await actionsApi.offerActionsGetV2(requestParameters);
            const actionsApiResponse: TActionList = {
                actionsNetworkCallId,
                actionList: actionsApiResponseObj.data as TCustomActionList,
            };
            return actionsApiResponse;
        } catch (error: any) {
            Log.e(ActionsServiceRepositoryImplV2.TAG, 'getOfferActionsFromServiceV2()', error);
            throw error;
        }
    }

    private async getRecordingActionsFromServiceV2(
        actionsParams: TActionsRecordingParams,
    ): Promise<TActionList> {
        try {
            const { recordingId, groups, channelNumber, screenType, actionsNetworkCallId } =
                actionsParams;
            const commonHeaders = new CommonHeaders(screenType).headers;

            const actionsApi = new ActionTagApi(
                new Configuration(this.getConfigurationParameters(commonHeaders)),
            );
            const reqParameters: RecordingActionsGetV2Request = {
                recordingId,
                ...this.getRequestParameters(commonHeaders, groups, channelNumber),
            };
            const actionsApiResponseObj = await actionsApi.recordingActionsGetV2(reqParameters);
            const actionsApiResponse: TActionList = {
                actionsNetworkCallId,
                actionList: actionsApiResponseObj.data as TCustomActionList,
            };
            return actionsApiResponse;
        } catch (error: any) {
            Log.e(ActionsServiceRepositoryImplV2.TAG, 'getRecordingActionsFromServiceV2()', error);
            throw error;
        }
    }

    private async getVodOfferActionsFromServiceV2(
        actionsParams: TActionsVodOfferParams,
    ): Promise<TActionList> {
        try {
            const { vodOfferId, groups, channelNumber, screenType, actionsNetworkCallId } =
                actionsParams;
            const commonHeaders = new CommonHeaders(screenType).headers;
            const requestParameters: VodOfferActionsGetV2Request = {
                vodOfferId,
                ...this.getRequestParameters(commonHeaders, groups, channelNumber),
            };

            const configuration = new Configuration(this.getConfigurationParameters(commonHeaders));
            const actionsApi = new ActionTagApi(configuration);
            const actionsApiResponseObj = await actionsApi.vodOfferActionsGetV2(requestParameters);
            const actionsApiResponse: TActionList = {
                actionsNetworkCallId,
                actionList: actionsApiResponseObj.data as TCustomActionList,
            };
            return actionsApiResponse;
        } catch (error: any) {
            Log.e(ActionsServiceRepositoryImplV2.TAG, 'getVodOfferActionsFromServiceV2()', error);
            throw error;
        }
    }

    /* @inheritdoc
     */
    public async fetchContentScreenDetailsContentActions(
        actionsParams: TActionsContentScreenContentIdParams,
    ): Promise<ContentScreenDetails> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async fetchContentScreenDetailsOfferActions(
        actionsParams: TActionsContentScreenOfferParams,
    ): Promise<ContentScreenDetails> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async fetchContentScreenDetailsRecordingActions(
        actionsParams: TActionsContentScreenRecordingParams,
    ): Promise<ContentScreenDetails> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async fetchOfferActions(actionsParams: TActionsParams): Promise<TActionList> {
        try {
            return await this.getOfferActionsFromServiceV2(actionsParams);
        } catch (error) {
            Log.e(ActionsServiceRepositoryImplV2.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchRecordingActions(
        actionsParams: TActionsRecordingParams,
    ): Promise<TActionList> {
        try {
            return await this.getRecordingActionsFromServiceV2(actionsParams);
        } catch (error) {
            Log.e(ActionsServiceRepositoryImplV2.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public fetchUnifiedCollectionActions(
        actionsParams: TActionsUnifiedCollectionParams,
    ): Promise<TActionList> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public fetchUnifiedCollectionScreenDetailsActions(
        actionsParams: TActionsParamsUCScreenDetails,
    ): Promise<TUnifiedCollectionScreenDetails> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async fetchVodOfferActions(actionsParams: TActionsVodOfferParams): Promise<TActionList> {
        try {
            return await this.getVodOfferActionsFromServiceV2(actionsParams);
        } catch (error) {
            Log.e(ActionsServiceRepositoryImplV2.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchContentActions(actionsParams: TActionsContentParams): Promise<TActionList> {
        throw new Error('Method not implemented.');
    }
}
